import { useSuggestFeature } from "@vixi/api";
import { useTranslation } from "@vixi/localization";
import { RequestedFeature } from "@vixi/types";
import {
  AttachButton,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Divider,
  TextArea,
  TextInput,
} from "@vixi/ui";
import { Controller, useForm } from "react-hook-form";

// Types
export interface FeatureFormValues {
  title: string;
  details: string;
  files?: File[];
}

interface RequestFeatureDialogProps {
  feature?: RequestedFeature | null;
  disabled?: boolean;
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { title: string; details: string; file?: File }) => void;
}

export default function RequestFeatureDialog({
  open,
  onClose,
  onSubmit,
}: RequestFeatureDialogProps) {
  const { t } = useTranslation();

  const { mutateAsync: submitFeature, isPending: isSubmitting } = useSuggestFeature();

  const featureForm = useForm<FeatureFormValues>({
    defaultValues: { title: "", details: "", files: [] },
    disabled: isSubmitting,
  });
  const { register, handleSubmit, reset, control } = featureForm;

  const onFormSubmit = async (data: FeatureFormValues) => {
    onSubmit(data);

    await submitFeature(data, {
      onSuccess: () => {
        onClose();
        reset();
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent showCloseButton={false} className="w-176.5 max-w-[calc(100%-60px)] px-0 pb-0">
        <form onSubmit={handleSubmit(onFormSubmit)}>
          <DialogHeader className="px-4">
            <DialogTitle className="text-center">{t("Feedback.Dialog.Title")}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-6.25 px-15.75 pt-7.5">
            {/* Short title */}
            <div>
              <p className="mb-0.25 font-semibold">{t("Feedback.Dialog.ShortTitleLabel")}</p>
              <TextInput
                className="mt-2"
                placeholder={t("Feedback.Dialog.ShortTitlePlaceholder")}
                {...register("title")}
              />
            </div>
            {/* Details */}
            <div className="mb-0.5">
              <p className="mb-2.75 font-semibold">{t("Feedback.Dialog.DetailsLabel")}</p>
              <p className="text-foreground-secondary font-medium">
                {t("Feedback.Dialog.DetailsDescription")}
              </p>
              <TextArea className="mt-3.5 w-full" rows={5} {...register("details")} />
            </div>
            {/* Attachment */}
            <div className="mb-6">
              <p className="mb-0.25 font-semibold">
                {t("Feedback.Dialog.AttachmentLabel")}{" "}
                <span className="text-foreground-secondary">({t("Optional")})</span>
              </p>
              <div className="mt-2">
                <Controller
                  name="files"
                  control={control}
                  render={({ field }) => (
                    <AttachButton multiple {...field}>
                      {t("Action.AttachPicture")}
                    </AttachButton>
                  )}
                />
              </div>
            </div>
          </div>
          <Divider />
          {/* Actions */}
          <DialogFooter className="mt-0.5 justify-end gap-2 px-13.25 py-6">
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                reset();
                onClose();
              }}
            >
              {t("Action.Cancel")}
            </Button>
            <Button
              disabled={!featureForm.formState.isValid || featureForm.formState.disabled}
              variant="contained"
              size="small"
              type="submit"
            >
              {t("Action.Submit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
